import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dayOf } from '@up2d8/ui';
import { startOfLocalDay, step, visibleDays } from '../../shared/calendar-range';
import { uiLocale } from '../../shared/format';
import {
  AgendaCalendar,
  CalendarToolbar,
  calendarTitle,
  rememberView,
  rememberedView,
  type AgendaView,
} from './agenda-calendar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AgendaItemKind,
  AgendaItemList,
  AgendaItemSummary,
  AttendanceList,
  AudienceSpec,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import {
  AdminIcon,
  AdminPage,
  Avatar,
  Badge,
  Btn,
  Card,
  CardHead,
  Drawer,
  Empty,
  Field,
  FilterButton,
  Input,
  Note,
  PageHead,
  Row,
  SearchField,
  Segmented,
  Textarea,
  useAdminToast,
  type BadgeTone,
} from '../admin-kit';
import { AudienceField } from '../beheer-communicatie/audience-field';
import { MenuButton } from '../beheer-communicatie/menu-button';
import { LoadError, Loading, usePageError } from '../beheer-communicatie/page-state';
import { downloadFile } from '../../shared/download';
import { API_BASE, useSession } from '../../shared/session';
import { formatDate, formatTime } from '../../shared/format';
import { audienceIncomplete } from '../../shared/audience-preview';

/**
 * Evenementen plannen en de aanwezigheid afvinken (Stap 25, nieuw ontwerp in BHR-5, frames C1–C3).
 *
 * Twee momenten van hetzelfde werk: vooraf plannen, en op de dag zelf afvinken. De lijst is het
 * scherm; plannen (C2) en afvinken (C3) gebeuren in een paneel, zodat de lijst zichtbaar blijft.
 *
 * Wat je ziet, volgt de rechten: "Nieuw evenement" en publiceren alleen met
 * `agenda.item.manage`, de afvinklijst met `agenda.attendance.view` en afvinken met
 * `agenda.attendance.record` (zie ook `admin-nav.ts`). De server beslist; dit scherm toont alleen
 * geen knop die toch met een `403` eindigt.
 */

const KINDS: AgendaItemKind[] = ['toolbox', 'training', 'event'];

/** De kleur van de soort (C1): Toolbox oranje, Opleiding blauw, Evenement navy. */
const KIND_TONE: Record<AgendaItemKind, BadgeTone> = {
  toolbox: 'attention',
  training: 'info',
  event: 'navy',
};

export function AgendaAdminPage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const toast = useAdminToast();
  const pageError = usePageError();
  const [planning, setPlanning] = useState(false);
  const [attendanceFor, setAttendanceFor] = useState<AgendaItemSummary | null>(null);
  const [kind, setKind] = useState<AgendaItemKind | 'all'>('all');
  // FBK-9: lijst, dag, week of maand. De keuze wordt per toestel onthouden.
  const [view, setViewState] = useState<AgendaView>(() => rememberedView());
  const [anchor, setAnchor] = useState(() => dayOf(new Date()));
  const [planStart, setPlanStart] = useState<string | null>(null);
  const setView = (next: AgendaView) => {
    setViewState(next);
    rememberView(next);
  };
  const range = view === 'list' ? null : visibleDays(view, anchor);

  const items = useQuery({
    queryKey: ['agenda-items', range?.first ?? null, range?.after ?? null],
    queryFn: async (): Promise<AgendaItemSummary[]> => {
      // De kalender vraagt alleen de zichtbare dagen op; de lijst wat er aankomt, zoals vroeger.
      const query = range
        ? {
            from: startOfLocalDay(range.first).toISOString(),
            to: startOfLocalDay(range.after).toISOString(),
          }
        : {};
      const { data, error } = await api.GET('/api/v1/agenda/items', {
        params: { query } as never,
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as AgendaItemList).items;
    },
  });

  const all = items.data ?? [];
  const shown = kind === 'all' ? all : all.filter((item) => item.kind === kind);

  return (
    <AdminPage>
      <PageHead
        title={t('agendaAdmin.title')}
        sub={t('beheer.communicatie.agenda.sub')}
        actions={
          has('agenda.item.manage') ? (
            <Btn
              icon={<AdminIcon name="plus" />}
              testId="agenda-new"
              onClick={() => setPlanning(true)}
            >
              {t('beheer.communicatie.agenda.new')}
            </Btn>
          ) : null
        }
      />

      {pageError.element}

      <Card>
        <div className="a-card-pad" style={{ paddingBottom: view === 'list' ? 0 : undefined }}>
          <CalendarToolbar
            view={view}
            onView={setView}
            title={view === 'list' ? '' : calendarTitle(view, anchor, uiLocale())}
            onPrevious={() => view !== 'list' && setAnchor(step(view, anchor, -1))}
            onNext={() => view !== 'list' && setAnchor(step(view, anchor, 1))}
            onToday={() => setAnchor(dayOf(new Date()))}
          />
        </div>
        {view !== 'list' ? (
          items.isError ? (
            <LoadError onRetry={() => void items.refetch()} error={items.error} />
          ) : (
            <div className="a-card-pad">
              <AgendaCalendar
                view={view}
                anchor={anchor}
                items={shown}
                onPickDay={(day) => {
                  setAnchor(day);
                  setView('day');
                }}
                onOpenItem={(item) => {
                  const summary = shown.find((candidate) => candidate.id === item.id);
                  if (summary && has('agenda.attendance.view')) setAttendanceFor(summary);
                }}
                onCreateAt={
                  has('agenda.item.manage')
                    ? (day, hour) => {
                        setPlanStart(`${day}T${hour < 10 ? '0' : ''}${hour}:00`);
                        setPlanning(true);
                      }
                    : undefined
                }
              />
            </div>
          )
        ) : null}
      </Card>

      {view === 'list' ? (
        <Card>
          <CardHead
            title={t('agendaAdmin.planned')}
            count={items.isSuccess ? shown.length : undefined}
            actions={
              <MenuButton
                label={t('agendaAdmin.kind')}
                radio
                trigger={(props) => (
                  <FilterButton
                    {...props}
                    label={t('agendaAdmin.kind')}
                    value={kind === 'all' ? undefined : t(`agenda.kind.${kind}`)}
                    active={kind !== 'all'}
                    expanded={props['aria-expanded']}
                    testId="agenda-kind-filter"
                  />
                )}
                items={[
                  {
                    key: 'all',
                    label: t('beheer.communicatie.agenda.allKinds'),
                    checked: kind === 'all',
                    onSelect: () => setKind('all'),
                  },
                  ...KINDS.map((option) => ({
                    key: option,
                    label: t(`agenda.kind.${option}`),
                    checked: kind === option,
                    onSelect: () => setKind(option),
                  })),
                ]}
              />
            }
          />

          {items.isPending ? (
            <Loading />
          ) : items.isError ? (
            <LoadError onRetry={() => void items.refetch()} error={items.error} />
          ) : shown.length === 0 ? (
            <Empty
              icon={<AdminIcon name="calendar" size={24} />}
              title={
                all.length === 0
                  ? t('agendaAdmin.emptyTitle')
                  : t('beheer.communicatie.agenda.emptyFilteredTitle')
              }
              text={
                all.length === 0
                  ? t('beheer.communicatie.agenda.emptyHint')
                  : t('beheer.communicatie.agenda.emptyFilteredHint')
              }
            />
          ) : (
            <ul className="c-list">
              {shown.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  onAttendance={() => setAttendanceFor(item)}
                  onPublished={() => {
                    pageError.clear();
                    toast.show(t('agendaAdmin.planned_ok'));
                  }}
                  onFailed={() => pageError.show(t('agendaAdmin.planFailed'))}
                />
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      <PlanDrawer
        open={planning}
        initialStart={planStart}
        onClose={() => {
          setPlanning(false);
          setPlanStart(null);
        }}
        onDone={(published) => {
          setPlanning(false);
          setPlanStart(null);
          pageError.clear();
          toast.show(published ? t('agendaAdmin.planned_ok') : t('agendaAdmin.draftSaved'));
        }}
      />

      {attendanceFor ? (
        <AttendanceDrawer item={attendanceFor} onClose={() => setAttendanceFor(null)} />
      ) : null}

      {toast.element}
    </AdminPage>
  );
}

/** "vr 18/09 · 06:15–06:30": de dag en het uur van een evenement, zoals in de mockup. */
function when(item: AgendaItemSummary): string {
  const day = formatDate(item.startsAt, { weekday: 'short', day: '2-digit', month: '2-digit' });
  return `${day} · ${formatTime(item.startsAt)}–${formatTime(item.endsAt)}`;
}

function ItemRow({
  item,
  onAttendance,
  onPublished,
  onFailed,
}: {
  item: AgendaItemSummary;
  onAttendance: () => void;
  onPublished: () => void;
  onFailed: () => void;
}) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const queryClient = useQueryClient();

  /** Een concept alsnog open zetten (Extra 6, punt 3): vanaf de lijst, daar vind je het terug. */
  const publish = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/agenda/items/{itemId}/publish', {
        params: { path: { itemId: item.id } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agenda-items'] });
      onPublished();
    },
    onError: onFailed,
  });

  // Het datumblok: maand (11 px, hoofdletters) boven de dag (Poppins 700, 22 px).
  const month = formatDate(item.startsAt, { month: 'short' }).replace(/\.$/, '');
  const day = formatDate(item.startsAt, { day: '2-digit' });

  const meta = [
    when(item),
    item.location || null,
    `${item.audienceDescription}${item.recipientCount !== null ? ` (${item.recipientCount})` : ''}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="a-item" data-testid="agenda-row">
      <div className="c-date" aria-hidden="true">
        <div className="a-stat-label">{month}</div>
        <div className="a-stat-num">{day}</div>
      </div>
      <div className="a-item-info">
        <div className="a-item-top">
          <Badge tone={KIND_TONE[item.kind]}>{t(`agenda.kind.${item.kind}`)}</Badge>
          {item.status === 'draft' ? <Badge>{t('agendaAdmin.draft')}</Badge> : null}
          {item.status === 'cancelled' ? (
            <Badge tone="danger">{t('agenda.cancelled')}</Badge>
          ) : null}
        </div>
        <div className="a-name">{item.title}</div>
        <div className="a-meta">{meta}</div>
      </div>
      <div className="c-item-side">
        {item.status === 'draft' && has('agenda.item.manage') ? (
          <Btn
            size="sm"
            icon={<AdminIcon name="send" />}
            disabled={publish.isPending}
            testId="agenda-publish-draft"
            onClick={() => publish.mutate()}
          >
            {t('beheer.communicatie.agenda.publishDraft')}
          </Btn>
        ) : null}
        {has('agenda.attendance.view') ? (
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="check" />}
            testId="agenda-attendance"
            onClick={onAttendance}
          >
            {t('agendaAdmin.showAttendance')}
          </Btn>
        ) : null}
      </div>
    </li>
  );
}

/** Nieuw evenement plannen (C2): een paneel, omdat het formulier lang is. */
function PlanDrawer({
  open,
  initialStart,
  onClose,
  onDone,
}: {
  open: boolean;
  /** Een klik op een leeg vak in de kalender (FBK-9): "2026-10-28T09:00", een uur lang. */
  initialStart?: string | null;
  onClose: () => void;
  onDone: (published: boolean) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<AgendaItemKind>('toolbox');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  useEffect(() => {
    if (!open || !initialStart) return;
    setStartsAt(initialStart);
    const [day, time] = initialStart.split('T') as [string, string];
    const hour = Math.min(23, Number(time.slice(0, 2)) + 1);
    setEndsAt(`${day}T${hour < 10 ? '0' : ''}${hour}:00`);
  }, [open, initialStart]);
  const [audience, setAudience] = useState<AudienceSpec>({ type: 'all' });
  const [error, setError] = useState<string | null>(null);
  // Het concept dat al aangemaakt is. Lukte alleen het publiceren niet, dan publiceert een tweede
  // klik dat concept, en maakt hij geen tweede evenement.
  const [createdId, setCreatedId] = useState<string | null>(null);

  const close = () => {
    setCreatedId(null);
    onClose();
  };

  /*
    De keuze is het argument van de mutatie en geen aparte staat (Extra 6, punt 3).

    Met `useState` in de knop en lezen in `mutationFn` zou het misgaan: een `setState` is niet
    meteen zichtbaar in dezelfde tik, dus de eerste klik op "Bewaar als concept" zou nog publiceren.
  */
  const plan = useMutation({
    mutationFn: async (meteenPubliceren: boolean) => {
      let itemId = createdId;
      if (!itemId) {
        const created = await api.POST('/api/v1/agenda/items', {
          body: {
            kind,
            title,
            description,
            location,
            // De invoer is lokale tijd zonder zone; de server verwacht een tijdstip met zone.
            startsAt: new Date(startsAt).toISOString(),
            endsAt: new Date(endsAt).toISOString(),
            rsvpEnabled: true,
            rsvpDeadline: null,
            audience,
          } as never,
        });
        if (created.error || !created.data) throw created.error ?? new Error('Aanmaken mislukt');
        itemId = (created.data as AgendaItemSummary).id;
        setCreatedId(itemId);
      }

      /*
        Concept of meteen publiceren (Extra 6, punt 3).

        De API kende het onderscheid al - `POST /agenda/items` maakt een concept, `publish` zet het
        open - maar het scherm publiceerde altijd meteen. Wie een evenement voorbereidt waarvan de
        datum nog kan schuiven, moest dus kiezen tussen "niets vastleggen" en "iedereen een melding
        sturen".
      */
      if (!meteenPubliceren) return;

      const published = await api.POST('/api/v1/agenda/items/{itemId}/publish', {
        params: { path: { itemId } },
      });
      if (published.error) throw published.error;
    },
    onSuccess: (_result, meteenPubliceren) => {
      setTitle('');
      setDescription('');
      setLocation('');
      setError(null);
      setCreatedId(null);
      void queryClient.invalidateQueries({ queryKey: ['agenda-items'] });
      onDone(meteenPubliceren);
    },
    onError: (failed) => {
      // Ook bij een fout: het concept kan al aangemaakt zijn en hoort dan in de lijst te staan.
      void queryClient.invalidateQueries({ queryKey: ['agenda-items'] });
      setError(problemDetail(failed) ?? t('agendaAdmin.planFailed'));
    },
  });

  // Ook geen lege doelgroep: "Kiezen" zonder keuze weigert de server (hertest 30 okt.).
  const complete =
    title.trim().length > 0 && startsAt !== '' && endsAt !== '' && !audienceIncomplete(audience);

  return (
    <Drawer
      open={open}
      onClose={close}
      title={t('agendaAdmin.planTitle')}
      testId="agenda-plan"
      footer={
        <>
          <Btn
            variant="sec"
            disabled={!complete || plan.isPending}
            testId="agenda-save-draft"
            onClick={() => plan.mutate(false)}
          >
            {t('agendaAdmin.saveDraft')}
          </Btn>
          <Btn
            type="submit"
            form="agenda-plan-form"
            icon={<AdminIcon name="send" />}
            disabled={!complete || plan.isPending}
            testId="agenda-publish"
          >
            {t('agendaAdmin.publish')}
          </Btn>
        </>
      }
    >
      <form
        id="agenda-plan-form"
        className="a-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !plan.isPending) plan.mutate(true);
        }}
      >
        <div className="a-field">
          <div className="a-label" id="agenda-kind-label">
            {t('agendaAdmin.kind')}
          </div>
          <Segmented
            labelledBy="agenda-kind-label"
            value={kind}
            onChange={setKind}
            options={KINDS.map((option) => ({
              value: option,
              label: t(`agenda.kind.${option}`),
            }))}
          />
        </div>

        <Field label={t('agendaAdmin.titleField')} htmlFor="agenda-title">
          {(props) => (
            <Input
              {...props}
              value={title}
              maxLength={200}
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('agendaAdmin.description')} optional htmlFor="agenda-description">
          {(props) => (
            <Textarea
              {...props}
              value={description}
              maxLength={5000}
              placeholder={t('beheer.communicatie.agenda.descriptionPlaceholder')}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('agendaAdmin.location')} htmlFor="agenda-location">
          {(props) => (
            <Input
              {...props}
              value={location}
              maxLength={200}
              placeholder={t('agendaAdmin.locationPlaceholder')}
              onChange={(event) => setLocation(event.target.value)}
            />
          )}
        </Field>

        <Row>
          <Field label={t('agendaAdmin.startsAt')} htmlFor="agenda-starts">
            {(props) => (
              <Input
                {...props}
                type="datetime-local"
                value={startsAt}
                onChange={(event) => setStartsAt(event.target.value)}
              />
            )}
          </Field>
          <Field label={t('agendaAdmin.endsAt')} htmlFor="agenda-ends">
            {(props) => (
              <Input
                {...props}
                type="datetime-local"
                value={endsAt}
                onChange={(event) => setEndsAt(event.target.value)}
              />
            )}
          </Field>
        </Row>

        <AudienceField value={audience} onChange={setAudience} testId="agenda-audience" />

        {error ? (
          <Note tone="danger" role="alert">
            {error}
          </Note>
        ) : null}
      </form>
    </Drawer>
  );
}

/**
 * De afvinklijst (C3).
 *
 * Hier staan namen, en dat is geen uitzondering op ADR-0014: een aanwezigheidslijst van een
 * veiligheidsmoment is per definitie een namenlijst. Ze zit achter `agenda.attendance.view`, en
 * afvinken achter `agenda.attendance.record`.
 */
function AttendanceDrawer({ item, onClose }: { item: AgendaItemSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const { api, getAccessToken, has } = useSession();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['agenda-attendance', item.id],
    queryFn: async (): Promise<AttendanceList> => {
      const { data, error: failed } = await api.GET('/api/v1/agenda/items/{itemId}/attendance', {
        params: { path: { itemId: item.id } },
      });
      if (failed || !data) throw failed ?? new Error('Ophalen mislukt');
      return data as AttendanceList;
    },
  });

  const record = useMutation({
    mutationFn: async (input: { personId: string; present: boolean }) => {
      const { error: failed } = await api.POST('/api/v1/agenda/items/{itemId}/attendance', {
        params: { path: { itemId: item.id } },
        body: input,
      });
      if (failed) throw failed;
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['agenda-attendance', item.id] });
    },
    onError: () => setError(t('agendaAdmin.recordFailed')),
  });

  /*
    De lijst als bestand (Extra 6, punt 3). Via de helper uit Extra 2: een kale `<a href download>`
    draagt het token niet en geeft `401`.
  */
  const exporteren = useMutation({
    mutationFn: () =>
      downloadFile({
        path: `/api/v1/agenda/items/${item.id}/attendance.csv`,
        fileName: `aanwezigheid-${item.id.slice(0, 8)}.csv`,
        token: getAccessToken(),
        baseUrl: API_BASE,
      }),
    onError: () => setError(t('errors.generic')),
  });

  const rows = list.data?.items ?? [];
  const present = rows.filter((row) => row.attendedAt !== null).length;
  const open = list.data?.recordingOpen ?? false;
  const needle = search.trim().toLowerCase();
  const shown =
    needle === ''
      ? rows
      : rows.filter(
          (row) =>
            row.displayName.toLowerCase().includes(needle) ||
            (row.employeeNumber ?? '').toLowerCase().includes(needle),
        );

  const sub = [
    item.title,
    formatDate(item.startsAt, { weekday: 'short', day: '2-digit', month: '2-digit' }),
    formatTime(item.startsAt),
    item.location || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('agendaAdmin.showAttendance')}
      sub={sub}
      testId="attendance-drawer"
      footer={
        <>
          <Btn
            variant="sec"
            icon={<AdminIcon name="download" />}
            disabled={exporteren.isPending}
            testId="attendance-export"
            onClick={() => exporteren.mutate()}
          >
            {t('agendaAdmin.exportAttendance')}
          </Btn>
          <Btn onClick={onClose}>{t('beheer.communicatie.done')}</Btn>
        </>
      }
    >
      {/*
        Het tijdvenster staat er altijd: buiten het venster is de lijst alleen-lezen, en dat hoort
        op het scherm te staan en niet pas in een foutmelding ná een tik. Is het dicht, dan oranje.
      */}
      <Note
        tone={list.data && !open ? 'attention' : 'neutral'}
        icon={<AdminIcon name="clock" size={18} />}
      >
        {t('agendaAdmin.windowClosed')}
      </Note>

      {error ? (
        <Note tone="danger" role="alert">
          {error}
        </Note>
      ) : null}

      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <LoadError onRetry={() => void list.refetch()} error={list.error} />
      ) : (
        <>
          <div className="a-pct">
            <div className="a-bar" style={{ flex: 1 }} aria-hidden="true">
              <span style={{ width: `${rows.length ? (present / rows.length) * 100 : 0}%` }} />
            </div>
            <b aria-live="polite">{t('agendaAdmin.presentOf', { present, total: rows.length })}</b>
          </div>

          <SearchField
            label={t('beheer.communicatie.agenda.search')}
            value={search}
            onChange={setSearch}
          />

          <div data-testid="attendance-list">
            {shown.length === 0 ? (
              <p className="a-hint">
                {rows.length === 0
                  ? t('beheer.communicatie.agenda.nobody')
                  : t('beheer.communicatie.agenda.noMatch')}
              </p>
            ) : (
              shown.map((row) => {
                const attended = row.attendedAt !== null;
                return (
                  <label
                    key={row.personId}
                    className="a-check c-attendance"
                    data-testid="attendance-row"
                  >
                    <input
                      type="checkbox"
                      checked={attended}
                      disabled={!open || record.isPending || !has('agenda.attendance.record')}
                      onChange={() => record.mutate({ personId: row.personId, present: !attended })}
                    />
                    <span className={attended ? 'a-box on' : 'a-box'} aria-hidden="true">
                      {attended ? <AdminIcon name="check" size={12} strokeWidth={3} /> : null}
                    </span>
                    <span className="a-person" style={{ flex: 1 }}>
                      <Avatar name={row.displayName} />
                      <span style={{ minWidth: 0 }}>
                        <span className="a-name" style={{ display: 'block' }}>
                          {row.displayName}
                        </span>
                        {row.employeeNumber || row.myAnswer === 'no' ? (
                          <span className="a-meta" style={{ display: 'block' }}>
                            {[
                              row.employeeNumber,
                              row.myAnswer === 'no' ? t('agendaAdmin.saidNo') : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </>
      )}
    </Drawer>
  );
}
